import { IUser, useStores } from '../models';
import { observer } from 'mobx-react-lite';
import { createContext, ReactNode, useEffect, useReducer } from 'react';
// @types
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../types/authentication';
// utils
import { isValidToken, tokenDestroy } from 'src/auth/util';
import { CallApiToStore, decryptBase64, encryptBase64 } from 'src/utils/common';

// ----------------------------------------------------------------------

export enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  WITHDRAWAL = 'WITHDRAWAL',
  PROFILE = 'PROFILE',
  RESET_PASSWORD = 'RESET_PASSWORD',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };

  [Types.LOGIN]: {
    user: AuthUser;
  };

  [Types.LOGOUT]: undefined;
  [Types.WITHDRAWAL]: undefined;

  [Types.PROFILE]: {
    user: AuthUser;
  };
  [Types.RESET_PASSWORD]: {
    user: AuthUser;
  };
};

type ActionsType = ActionMap<Payload>[keyof ActionMap<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthState, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (
    action.type === Types.LOGIN ||
    action.type === Types.PROFILE ||
    action.type === Types.RESET_PASSWORD
  ) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT || action.type === Types.WITHDRAWAL) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

export const AuthProvider = observer(({ children }: { children: ReactNode }) => {
  const { userStore, loadingStore, responseStore } = useStores();

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = sessionStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          //로그인에 필요한 정보 등록(ex 로그인 사용자 정보 Set)
          CallApiToStore(userStore.getProfile(), 'api', loadingStore)
            .then(() => {
              if (responseStore.responseInfo.resultCode == 'S') {
                dispatch({
                  type: Types.INITIAL,
                  payload: {
                    isAuthenticated: true,
                    user: userStore.user,
                  },
                });
              } else {
                tokenDestroy();
                userStore.setProps({
                  user: {} as IUser,
                });
                dispatch({
                  type: Types.INITIAL,
                  payload: {
                    isAuthenticated: false,
                    user: null,
                  },
                });
              }
            })
            .catch((e) => console.log(e));
        } else {
          // 로그인 상태 유지
          const keepLogin = localStorage.getItem('keepLogin')
            ? JSON.parse(localStorage.getItem('keepLogin')!)
            : false;
          if (keepLogin) {
            const user = JSON.parse(decryptBase64(localStorage.getItem('rememberMe')!));
            login(user.loginId, user.pwd, true, true);
          } else {
            tokenDestroy();
            userStore.setProps({
              user: {} as IUser,
            });
            dispatch({
              type: Types.INITIAL,
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        }
      } catch (err) {
        console.error(err);
        tokenDestroy();
        userStore.setProps({
          user: {} as IUser,
        });
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (id: string, password: string, keepLogin: boolean, isAuto?: boolean) => {
    const user = {
      loginId: id,
      pwd: password,
    } as IUser;
    CallApiToStore(userStore.login(user), 'api', loadingStore)
      .then(() => {
        if (responseStore.responseInfo.resultCode == 'S') {
          if (isAuto) { // 로그인 상태 유지를 선택한 사용자가 향후 타는 로직
            dispatch({
              type: Types.INITIAL,
              payload: {
                isAuthenticated: true,
                user: userStore.user,
              },
            });
          } else {
            if (keepLogin) {
              localStorage.setItem('keepLogin', 'true');
              localStorage.setItem('rememberMe', encryptBase64(JSON.stringify(user))); // 아이디 / 비밀번호 암호화 저장
            }
            dispatch({
              type: Types.LOGIN,
              payload: {
                user: userStore.user,
              },
            });
          }
        } else {
          return;
        }
      })
      .catch((e) => console.log(e));
  };

  const logout = async () => {
    CallApiToStore(userStore.logout(), 'api', loadingStore)
      .then(() => {
        if (responseStore.responseInfo.resultCode == 'S') {
          dispatch({
            type: Types.LOGOUT,
          });
        } else {
          return;
        }
      })
      .catch((e) => console.log(e));
  };

  const resetPassword = async (user: IUser) => {
    let result = 'F';
    await CallApiToStore(userStore.resetPwdByProfile(user), 'api', loadingStore)
      .then(() => {
        if (responseStore.responseInfo.resultCode == 'S') {
          // 로그인 유지 - 새로운 비밀번호로 변경하여 저장
          const keepLogin = localStorage.getItem('keepLogin')
            ? JSON.parse(localStorage.getItem('keepLogin')!)
            : false;
          if (keepLogin) {
            const newUser = {
              loginId: userStore.user.loginId,
              pwd: user.newPwd,
            } as IUser;
            localStorage.setItem('rememberMe', encryptBase64(JSON.stringify(newUser)));
          }
          dispatch({
            type: Types.RESET_PASSWORD,
            payload: {
              user: userStore.user,
            },
          });
          result = 'S';
        } else {
          return;
        }
      })
      .catch((e) => console.log(e));

    return result;
  };

  const updateProfile = async (user: IUser) => {
    let result = 'F';
    await CallApiToStore(userStore.updateProfile(user), 'api', loadingStore)
      .then(() => {
        if (responseStore.responseInfo.resultCode == 'S') {
          dispatch({
            type: Types.PROFILE,
            payload: {
              user: userStore.user,
            },
          });
          result = 'S';
        } else {
          return;
        }
      })
      .catch((e) => console.log(e));

    return result;
  };

  const withdrawal = async () => {
    CallApiToStore(userStore.withdrawal(), 'api', loadingStore)
      .then(() => {
        if (responseStore.responseInfo.resultCode == 'S') {
          dispatch({
            type: Types.WITHDRAWAL,
          });
        } else {
          return;
        }
      })
      .catch((e) => console.log(e));
  };

  const getProfile = async () => {
    let result = 'F';
    await CallApiToStore(userStore.getProfile(), 'api', loadingStore)
      .then(() => {
        if (responseStore.responseInfo.resultCode == 'S') {
          dispatch({
            type: Types.PROFILE,
            payload: {
              user: userStore.user,
            },
          });
          result = 'S';
        } else {
          return;
        }
      })
      .catch((e) => console.log(e));

    return result;
  };

  return (
    <AuthContext.Provider
      value={{
        isInitialized: state.isInitialized,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        method: 'jwt',
        login,
        logout,
        resetPassword,
        updateProfile,
        withdrawal,
        getProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
});
